import { Button, CircularProgress, Grid, Tab, Tabs } from '@mui/material';
import PaymentHistoryTable from 'components/ui/tables/PaymentHistoryTable';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import { Fragment, memo, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import REPAIR_TYPE from 'shared/repairTypeList';
import getLabelByTypeList from 'utils/getLabelByTypeList';
import SuccessTooltip from 'components/ui/tooltips/SuccessTooltip';
import ContractPaymentListModal from '../contract/ContractPaymentListModal';
import InfoTooltip from 'components/ui/tooltips/InfoTooltip';
import ImagePreviewDialog from 'components/ui/dialogs/ImagePreviewDialog';
import { Trans, useTranslation } from 'react-i18next';
import useAuth from 'hooks/useAuth';

const ClientContract = ({
  contractsQuery: { isLoading, isFetching, data, refetch },
}) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const [{ user }] = useAuth();
  const [open, setOpen] = useState(false);
  const [contractIndex, setContractIndex] = useState(0);
  const [openHomeLayoutImageDialog, setOpenHomeLayoutImageDialog] =
    useState(false);

  const contractData = data?.length ? data?.[contractIndex] : {};

  const newSearch = useMemo(() => {
    if (search?.size > 0) {
      return `?${search?.toString()}&token=${user?.access_token}`;
    }
    return `?token=${user?.access_token}`;
  }, [search, user?.access_token]);

  return (
    <div className='client-contracts-wrapper mt-6 pb-6'>
      <div className='client-contracts-title text-xl font-medium text-center mb-4'>
        {t('client.view.clientContracts')}
      </div>
      {isLoading || isFetching ? (
        <div className='circular-progress-box py-5'>
          <CircularProgress size={35} />
        </div>
      ) : data?.length > 0 ? (
        <Fragment>
          <Tabs
            value={contractIndex}
            onChange={(event, newValue) => setContractIndex(newValue)}
            className='client-contracts-tabs'
          >
            {data?.map((contract, index) => (
              <Tab
                label={t('client.view.contract', {
                  value: contract?.name,
                })}
                value={index}
                key={`contract-tab-${contract?.id}`}
              />
            ))}
          </Tabs>

          <div className='component-add-edit-wrapper p-4 base-border bg-white shadow-md mt-4'>
            {contractData && (
              <Fragment>
                <div className='component-add-edit-header mt-3'>
                  <Grid
                    container
                    spacing={{ xs: 2, sm: 3, lg: 3 }}
                    rowSpacing={1}
                    columns={{ xs: 12, sm: 12, lg: 15 }}
                  >
                    <Grid item={true} lg={3} sm={6} xs={12}>
                      <Button
                        color='secondary'
                        variant='contained'
                        fullWidth
                        className='!h-full'
                      >
                        <div className='flex flex-col p-2'>
                          <span className='text-lg leading-5 mb-1'>
                            {t('client.contract.header.sum')}
                          </span>
                          <span className='text-sm'>
                            <CurrencyFormat
                              value={contractData?.sum}
                              suffix={
                                contractData?.isvalute == '1' ? ' $' : ' UZS'
                              }
                            />
                          </span>
                        </div>
                      </Button>
                    </Grid>
                    <Grid item={true} lg={3} sm={6} xs={12}>
                      <Button
                        color='info'
                        variant='contained'
                        fullWidth
                        className='!h-full'
                      >
                        <div className='flex flex-col p-2'>
                          <span className='text-lg leading-5 mb-1'>
                            {t('client.contract.header.startPrice')}
                          </span>
                          <span className='text-sm'>
                            <CurrencyFormat
                              value={contractData?.start_price}
                              suffix={
                                contractData?.isvalute == '1' ? ' $' : ' UZS'
                              }
                            />
                          </span>
                        </div>
                      </Button>
                    </Grid>
                    <Grid item={true} lg={3} sm={6} xs={12}>
                      <Button
                        color='warning'
                        variant='contained'
                        fullWidth
                        className='!h-full'
                      >
                        <div className='flex flex-col p-2'>
                          <span className='text-lg leading-5 mb-1'>
                            <Trans i18nKey='client.contract.header.priceForSquare'>
                              1 m<sup>2</sup> uchun to'lov miqdori
                            </Trans>
                          </span>
                          <span className='text-sm'>
                            <CurrencyFormat
                              value={contractData?.price}
                              suffix={
                                contractData?.isvalute == '1' ? ' $' : ' UZS'
                              }
                            />
                          </span>
                        </div>
                      </Button>
                    </Grid>
                    <Grid item={true} lg={3} sm={6} xs={12}>
                      <Button
                        color='success'
                        variant='contained'
                        fullWidth
                        className='!h-full'
                      >
                        <div className='flex flex-col p-2'>
                          <span className='text-lg leading-5 mb-1'>
                            {t('client.contract.header.discount')}
                          </span>
                          <span className='text-sm'>
                            <CurrencyFormat
                              value={contractData?.discount}
                              suffix={
                                contractData?.isvalute == '1' ? ' $' : ' UZS'
                              }
                            />
                          </span>
                        </div>
                      </Button>
                    </Grid>
                    <Grid item={true} lg={3} sm={6} xs={12}>
                      <Button
                        color='error'
                        variant='contained'
                        fullWidth
                        className='!h-full'
                      >
                        <div className='flex flex-col p-2'>
                          <span className='text-lg leading-5 mb-1'>
                            {t('client.contract.header.leftPrice')}
                          </span>
                          <span className='text-sm'>
                            <CurrencyFormat
                              value={contractData?.left}
                              allowNegative={true}
                              suffix={
                                contractData?.isvalute == '1' ? ' $' : ' UZS'
                              }
                            />
                          </span>
                        </div>
                      </Button>
                    </Grid>
                  </Grid>
                </div>
                {contractData?.comment && (
                  <div className='contract-comment my-shadow-2 rounded-lg p-4 w-full mt-4 mb-1'>
                    <div>
                      <span className='font-medium text-base-color'>
                        {t('client.contract.header.comment')}:
                      </span>{' '}
                      {contractData?.comment}
                    </div>
                  </div>
                )}
                <div className='component-add-edit-body'>
                  <div className='py-3 flex flex-row'>
                    <div>
                      <div className='base-data w-full'>
                        {contractData?.homelist?.map(({ home }) => (
                          <div
                            key={home?.id}
                            className='home-data flex items-center justify-between base-border shadow-md p-4 w-full mb-4'
                          >
                            <div className='home-data-item flex flex-col'>
                              <span className='data-item-title text-base font-medium text-base-color'>
                                {t('client.contract.homeDetail.objectName')}:
                              </span>
                              <span className='data-item-value text-base'>
                                {home?.blocks?.objects?.name}
                              </span>
                            </div>
                            <div className='home-data-item flex flex-col'>
                              <span className='data-item-title text-base font-medium text-base-color'>
                                {t('client.contract.homeDetail.blockName')}:
                              </span>
                              <span className='data-item-value text-base'>
                                {home?.blocks?.name}
                              </span>
                            </div>
                            <div className='home-data-item flex flex-col'>
                              <span className='data-item-title text-base font-medium text-base-color'>
                                {t('client.contract.homeDetail.homeNumber')}:
                              </span>
                              <span className='data-item-value text-base'>
                                {home?.number}
                              </span>
                            </div>
                            <div className='home-data-item flex flex-col'>
                              <span className='data-item-title text-base font-medium text-base-color'>
                                {t('client.contract.homeDetail.stage')}:
                              </span>
                              <span className='data-item-value text-base'>
                                {home?.stage}
                              </span>
                            </div>
                            <div className='home-data-item flex flex-col'>
                              <span className='data-item-title text-base font-medium text-base-color'>
                                {t('client.contract.homeDetail.rooms')}:
                              </span>
                              <span className='data-item-value text-base'>
                                {home?.rooms}
                              </span>
                            </div>
                            <div className='home-data-item flex flex-col'>
                              <span className='data-item-title text-base font-medium text-base-color'>
                                {t('client.contract.homeDetail.area')}:
                              </span>
                              <span className='data-item-value text-base'>
                                {home?.square}{' '}
                                <Trans i18nKey='common.global.meter'>
                                  m<sup>2</sup>
                                </Trans>
                              </span>
                            </div>
                            <div className='home-data-item flex flex-col'>
                              <span className='data-item-title text-base font-medium text-base-color'>
                                {t('client.contract.homeDetail.isrepaired')}:
                              </span>
                              <span className='data-item-value text-base'>
                                {getLabelByTypeList(
                                  REPAIR_TYPE,
                                  contractData?.isrepaired
                                )}
                              </span>
                            </div>
                            <div className='home-data-item flex flex-col'>
                              <span className='data-item-title text-base font-medium text-base-color'>
                                {t('common.button.homePlan')}:
                              </span>
                              <span className='data-item-value text-base'>
                                <Button
                                  type='button'
                                  variant='contained'
                                  className='!-mb-[10px] !w-[100px]'
                                  disabled={!(home?.plan && home?.plan?.link)}
                                  onClick={() =>
                                    setOpenHomeLayoutImageDialog(true)
                                  }
                                >
                                  <i className='bi bi-image text-lg' />
                                </Button>
                              </span>
                            </div>
                          </div>
                        ))}

                        <div className='contract-staff-details !base-border shadow-md p-4 w-full mt-4 mb-1 border border-base-color bg-base-color-active-deprecated-f-30'>
                          <div>
                            <span className='font-medium text-base-color'>
                              {t('client.contract.staff')}:
                            </span>{' '}
                            {contractData?.staff?.name}
                          </div>
                        </div>

                        <div className='mt-4 mb-6'>
                          {contractData?.id && (
                            <Fragment>
                              <div className='payment-history-title my-2 text-lg font-medium text-base-color'>
                                {t('client.contract.paymentHistory')}
                              </div>
                              <PaymentHistoryTable
                                dataPath={`dictionary/paymentscontract/${contractData?.id}`}
                              />
                            </Fragment>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='actions-box w-[280px] ml-6'>
                      <div className='p-4 !base-border !shadow-md flex gap-4 items-center justify-center'>
                        <InfoTooltip
                          arrow={true}
                          placement='top'
                          title={t('shaxmatka.homeDetail.goToContract')}
                        >
                          <Link
                            to={`/admin/contract/view/${contractData?.id}`}
                            className='no-underline'
                          >
                            <Button
                              variant='action'
                              color='info'
                              onClick={refetch}
                            >
                              <i className='bi bi-box-arrow-up-right' />
                            </Button>
                          </Link>
                        </InfoTooltip>
                        <InfoTooltip
                          arrow={true}
                          placement='top'
                          title={t('client.contract.action.refresh')}
                        >
                          <Button
                            variant='action'
                            color='info'
                            onClick={refetch}
                          >
                            <i className='bi bi-arrow-repeat' />
                          </Button>
                        </InfoTooltip>
                        <SuccessTooltip
                          arrow={true}
                          placement='top'
                          title={t('client.contract.action.download')}
                        >
                          <Link
                            to={`${process.env.REACT_APP_BACKEND_URL}/doc/${contractData?.id}${newSearch}`}
                            className='no-underline'
                          >
                            <Button variant='action' color='success'>
                              <i className='bi bi-download' />
                            </Button>
                          </Link>
                        </SuccessTooltip>
                      </div>

                      <Button
                        color='primary'
                        variant='outlined'
                        fullWidth
                        className='!py-2 !my-4 !h-auto'
                        onClick={() => setOpen(true)}
                      >
                        <div className='flex flex-col'>
                          <i className='bi bi-bookmarks text-2xl' />
                          <span className='text-base'>
                            {t('client.contract.paymentTable')}
                          </span>
                        </div>
                      </Button>

                      {contractData?.status == '2' && (
                        <Button
                          color='error'
                          variant='contained'
                          fullWidth
                          className='!mb-4'
                        >
                          <span>{t('client.contract.completed')}</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Fragment>
            )}
            {open && (
              <ContractPaymentListModal
                open={open}
                setOpen={setOpen}
                data={contractData}
              />
            )}

            {openHomeLayoutImageDialog &&
              contractData?.homes?.plan &&
              contractData?.homes?.plan?.link && (
                <ImagePreviewDialog
                  open={openHomeLayoutImageDialog}
                  setOpen={setOpenHomeLayoutImageDialog}
                  url={contractData?.homes?.plan?.link}
                />
              )}
          </div>
        </Fragment>
      ) : (
        <div className='mt-6 p-4 rounded-lg my-shadow-2'>
          <span className='no-data-found-wrapper'>
            <i className='bi bi-exclamation-octagon text-xl mr-1 leading-3' />{' '}
            {t('client.view.noContractsFound')}
          </span>
        </div>
      )}
    </div>
  );
};

export default memo(ClientContract);
